import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router'
import { inject, observer } from 'mobx-react'
import { Formik, Form, Field } from 'formik'
import yup from 'yup'
import pick from 'lodash/pick'
import assign from 'lodash/assign'
import uniq from 'lodash/uniq'
import compact from 'lodash/compact'
import { toJS, extendObservable } from 'mobx'

import { convertStaticDataToOptions } from '@atheneum/api/dist/lib'

import { clientContactApi, getOfficeSuggestions, clientRecords, employeeApi, officeApi, userApi } from '@atheneum/api'

import ProfessionalLinkFields from '@components/Form/ProfessionalLinkFields'
import RadioGroup from '@components/Form/RadioGroup'
import KmSpecificAccounts from '@components/Form/KmSpecificAccounts'
import Button from '@components/Button'
import WarningIcon from '@components/Icon/Warning'
import InfoBox from '@components/InfoBox'
import { Autocomplete, Dropdown } from '@components/Form/Autocomplete'
import Input from '@components/Form/Input'
import PhoneFields from '@components/Form/PhoneFields'
import EmailFields from '@components/Form/EmailFields'
import Checkbox from '@components/Checkbox'
import AddressFields from '@components/Form/AddressFields'
import ACL from '@components/ACL'

class ClientContactForm extends Component {
  static propTypes = {
    contact: PropTypes.object,
    sidebarStore: PropTypes.object,
    staticData: PropTypes.object,
    modalStore: PropTypes.object,
    userStore: PropTypes.object,
    office: PropTypes.object,
    onSaveSuccess: PropTypes.func
  }

  constructor(props) {
    super(props)

    extendObservable(this, {
      address: {},
      allAccounts: [],
      error: null,
      contactOptions: ['All accounts', 'Specific accounts'],
      employeeOptions: []
    })
  }

  componentDidMount() {
    const { contact } = this.props
    if (contact) {
      const currentParAccId = contact.client.account.parentAccountId
      this.address = contact.client.office.address
      if (currentParAccId) {
        this.getAllAccounts(currentParAccId)
      }
    }
    this.loadEmployeeOptions()
  }

  handleTitleChange = (title, form) => {
    form.setFieldValue('title', title, false)
  }

  onPositionChange = (value, form) => {
    if (!value) return
    form.setFieldValue('client.position', value, false)
  }

  onAtheneumContactChange = (atheneumContact, form) => {
    if (atheneumContact) {
      this.setAtheneumContactTo(form, {
        id: atheneumContact.id,
        fullName: atheneumContact.fullName
      })
    } else {
      this.setAtheneumContactTo(form, { id: null, fullName: '' })
    }
  }

  handleOfficeAutocomplete = input => {
    if (!input) return Promise.resolve([])

    let payload = {}
    const { contact } = this.props

    if (contact) {
      const parentAccountId =
        contact.client.account.parentAccountId || undefined
      payload = { parentAccountId: parentAccountId }
    }

    return getOfficeSuggestions(input, payload)
  }

  onOfficeChange = (office, form) => {
    form.setFieldValue('client.office', office, false)
    form.setFieldTouched('client.office', true, true)

    if (!office) return

    form.setFieldValue('client.officeId', office.id, false)

    return officeApi.getOffice(office.id).then(({ account, address }) => {
      this.address = address
      if (form.values.client.officeAddress) {
        this.setClientAddress(address, form)
      }
      this.setAccount(account, form)
    })
  }

  loadEmployeeOptions = async () => {
    const { EMPLOYEE_POSITIONS } = this.props.staticData.CONST
    const employees = await employeeApi.getByPosition([
      EMPLOYEE_POSITIONS.BD,
      EMPLOYEE_POSITIONS.TEAM_LEADER,
      EMPLOYEE_POSITIONS.ACCOUNT_MANAGER,
      EMPLOYEE_POSITIONS.PRINCIPAL
    ])
    this.employeeOptions.replace(employees)
  }

  setAccount = (account, form) => {
    const currentParAccId = form.values.client.account.parentAccountId
    const { contact } = this.props

    if (!contact && currentParAccId !== account.parentAccountId) {
      form.setFieldValue('kmSpecificAccounts', [{ id: '' }], false)
    }

    const changedAccount = {
      id: account.id,
      companyName: account.companyName,
      parentAccountId: account.parentAccountId
    }

    form.setFieldValue('client.account', changedAccount, false)
    form.setFieldValue('client.accountId', changedAccount.id, false)

    this.getAllAccounts(account.parentAccountId)
  }

  getAllAccounts = parentAccountId => {
    clientRecords.getAccounts(parentAccountId, { expanded: false }).then(res => {
      this.allAccounts.replace(res)
    })
  }

  clientOfficeSidebarLink = form => {
    const officeId = form.values.client.office.id
    this.props.sidebarStore.openSidebar({
      id: officeId,
      template: 'client-office'
    })
  }

  clientAccountSidebarLink = form => {
    const accountId = form.values.client.account.id
    this.props.sidebarStore.openSidebar({
      id: accountId,
      template: 'client-account'
    })
  }

  toggleSameAddress = (isToggled, form) => {
    form.setFieldValue('client.officeAddress', isToggled, false)
    if (isToggled) {
      this.setClientAddress(this.address, form)
    }
  }

  setClientAddress = (address, form) => {
    form.setFieldValue('address.address1', address.address1, false)
    form.setFieldValue('address.city', address.city, false)
    form.setFieldValue('address.postalCode', address.postalCode, false)
    form.setFieldValue('address.state', address.state, false)
    form.setFieldValue('address.countryId', address.countryId, false)
    form.setFieldValue('address.timezoneId', address.timezoneId, false)
  }

  enableKmPermissions = (isToggled, form) => {
    form.setFieldValue('client.knowledgeManager', isToggled, false)
    form.setFieldValue('type', 'All accounts', false)
    form.setFieldValue('client.global', true, false)

    this.handleAtheneumContactToggle(isToggled, form)
  }

  handleAtheneumContactToggle = (isToggled, form) => {
    if (isToggled) {
      const { EMPLOYEE_POSITIONS } = this.props.staticData.CONST
      const { user } = this.props.userStore

      // do not add an Admin here because he will not be on the dropdown anyway
      if (
        [
          EMPLOYEE_POSITIONS.BD,
          EMPLOYEE_POSITIONS.TEAM_LEADER,
          EMPLOYEE_POSITIONS.ACCOUNT_MANAGER,
          EMPLOYEE_POSITIONS.PRINCIPAL
        ].includes(user.currentPosition.employeePositionId)
      ) {
        this.setAtheneumContactTo(form, pick(user, ['id', 'fullName']))
      }
    } else {
      this.setAtheneumContactTo(form, { id: null, fullName: '' })
    }
  }

  setAtheneumContactTo = (form, atheneumContact) => {
    form.setFieldValue('client.atheneumContactId', atheneumContact.id, false)
    form.setFieldValue('client.atheneumContact.id', atheneumContact.id, false)
    form.setFieldValue(
      'client.atheneumContact.fullName',
      atheneumContact.fullName,
      false
    )
  }

  saveClientContact = contactData => {
    const { contact } = this.props
    let savedData = contactData
    let clientData = contactData.client
    if (contactData.type === 'All accounts') {
      clientData = { ...clientData, global: true }
      savedData = { ...contactData, client: clientData }
    } else {
      clientData = { ...clientData, global: false }
      savedData = { ...contactData, client: clientData }
    }

    return contact ? clientContactApi.updateClientContact(contact.id, savedData) : clientContactApi.createClientContact(savedData)
  }

  render() {
    const {
      contact,
      onSaveSuccess,
      modalStore,
      staticData,
      userStore
    } = this.props
    const isAdmin = userStore.user.permissions.find(p => p.name === 'Admin')
    return (
      <div>
        <Formik
          enableReinitialize
          initialValues={this.getInitialValues()}
          validationSchema={this.getValidationSchema()}
          onSubmit={(values, actions) => {
            this.saveClientContact(values)
              .then(savedContact => {
                onSaveSuccess(savedContact)
              })
              .catch(({ message }) => {
                this.error = message
                actions.setSubmitting(false)
              })
          }}
          render={form => (
            <div className="expert-form">
              <h1 className="expert-form__title">
                {contact
                  ? 'Edit client contact details'
                  : 'Create new client contact'}
              </h1>
              {this.error ? (
                <InfoBox theme="red" icon={<WarningIcon />}>
                  {this.error}
                </InfoBox>
              ) : null}
              <Form onSubmit={form.handleSubmit}>
                <div className="expert-form__section">
                  <h2 className="expert-form__subtitle">Basic information</h2>
                  <Field
                    extraWide
                    name="title"
                    label="Title"
                    data-cy="title-dropdown"
                    placeholder="Select title"
                    onChange={title => this.handleTitleChange(title, form)}
                    options={staticData.titles}
                    component={Dropdown}
                  />
                  <Field
                    name="firstName"
                    label="First name *"
                    component={Input}
                  />
                  <Field
                    name="lastName"
                    label="Last name *"
                    component={Input}
                  />
                  <Field
                    name="originalName"
                    label="Name in original language"
                    component={Input}
                  />

                  <Field
                    creatable
                    name="client.position"
                    label="Position"
                    placeholder=""
                    itemToString={item => item}
                    loadOptions={clientContactApi.getClientPositionSuggestions}
                    onChange={value => {
                      this.onPositionChange(value, form)
                    }}
                    component={Autocomplete}
                  />

                  <Field
                    name="client.office"
                    label="Office *"
                    placeholder=""
                    data-cy="client-office-autocomplete"
                    itemToString={item => item && item.officeName}
                    loadOptions={this.handleOfficeAutocomplete}
                    onChange={value => this.onOfficeChange(value, form)}
                    linkLabel="Details"
                    linkCallback={() => this.clientOfficeSidebarLink(form)}
                    component={Autocomplete}
                  />

                  <Field
                    name="client.account.companyName"
                    label="Account"
                    component={Input}
                    linkLabel="Details"
                    linkCallback={() => this.clientAccountSidebarLink(form)}
                    disabled
                  />
                </div>
                <div className="expert-form__section">
                  <h2 className="expert-form__subtitle">Contact information</h2>
                  <PhoneFields
                    form={form}
                    name="address.phones"
                    label="Phones *"
                    phoneNotRepeating={form.errors.phoneNotRepeating}
                  />
                  <EmailFields
                    form={form}
                    primaryEmail="email"
                    additionalEmails="address.emails"
                    emailNotRepeating={form.errors.emailNotRepeating}
                    label="Emails *"
                    primaryEmailIsDisabled={
                      !isAdmin && form.values.client.complianceAuditor
                    }
                  />
                  <ProfessionalLinkFields
                    form={form}
                    name="clientLinks"
                    label="Professional links"
                  />
                  <div className="m-t-lg">
                    <Checkbox
                      value={form.values.client.officeAddress}
                      label="Same address information as office"
                      onChange={isToggled =>
                        this.toggleSameAddress(isToggled, form)
                      }
                    />
                    <AddressFields
                      form={form}
                      name="address"
                      cityLabel="City / Postal code *"
                      timezone
                      postalCode
                      editAddress
                      secondaryAddress
                      disabled={form.values.client.officeAddress}
                    />
                  </div>
                </div>
                <div className="expert-form__section">
                  <h2 className="expert-form__subtitle">
                    Additional permissions
                  </h2>
                  <ACL roles={['Admin']}>
                    <div>
                      <Checkbox
                        value={form.values.client.complianceAuditor}
                        label="Enable compliance audit permissions"
                        onChange={isChecked =>
                          form.setFieldValue(
                            'client.complianceAuditor',
                            isChecked,
                            false
                          )
                        }
                      />
                    </div>
                  </ACL>
                  <Checkbox
                    value={form.values.client.knowledgeManager}
                    label="Enable KM permissions"
                    onChange={isToggled =>
                      this.enableKmPermissions(isToggled, form)
                    }
                  />
                  <div className="m-b-xl">
                    <Field
                      name="type"
                      options={convertStaticDataToOptions(this.contactOptions)}
                      component={RadioGroup}
                      disabled={!form.values.client.knowledgeManager}
                    />
                    {form.values.type === 'Specific accounts' &&
                      form.values.client.knowledgeManager && (
                        <KmSpecificAccounts
                          form={form}
                          name="kmSpecificAccounts"
                          label="Specific accounts"
                          parentAccountId="client.account.parentAccountId"
                          allAccounts={this.allAccounts}
                        />
                      )}
                    {(form.values.client.knowledgeManager ||
                      form.values.client.complianceAuditor) && (
                      <Field
                        form={form}
                        name="client.atheneumContact"
                        label="Atheneum contact"
                        itemToString={item => item && item.fullName}
                        onChange={value => {
                          this.onAtheneumContactChange(value, form)
                        }}
                        options={this.employeeOptions}
                        component={Autocomplete}
                      />
                    )}
                  </div>
                  <p className="expert-form__label">
                    Fields marked with * are required
                  </p>
                </div>
                <div className="expert-form__section p-b-0">
                  <Button primary submit onClick={this.onSubmit}>
                    Save
                  </Button>
                  <Button onClick={modalStore.close}>Cancel</Button>
                </div>
              </Form>
            </div>
          )}
        />
      </div>
    )
  }

  getInitialValues = () => {
    const { contact, office } = this.props

    return contact
      ? assign(
          pick(toJS(contact), [
            'title',
            'firstName',
            'lastName',
            'originalName',
            'client.position',
            'client.officeId',
            'client.accountId',
            'client.office.id',
            'client.office.address',
            'client.office.officeName',
            'client.account.id',
            'client.account.companyName',
            'client.account.parentAccountId',
            'client.complianceAuditor',
            'client.knowledgeManager',
            'client.global',
            'client.officeAddress',
            'client.atheneumContactId',
            'client.atheneumContact.id',
            'client.atheneumContact.fullName',
            'address.phones',
            'email',
            'clientLinks',
            'country',
            'address.emails',
            'address.address1',
            'address.address2',
            'address.city',
            'address.postalCode',
            'address.state',
            'address.countryId',
            'address.timezoneId',
            'kmSpecificAccounts'
          ]),
          {
            type: contact.client.global ? 'All accounts' : 'Specific accounts'
          }
        )
      : {
          title: '',
          firstName: '',
          lastName: '',
          originalName: '',
          client: {
            position: '',
            account: {
              id: office ? office.accountId : '',
              companyName: office ? office.account.companyName : '',
              parentAccountId: ''
            },
            office: {
              id: office ? office.id : '',
              officeName: office ? office.officeName : '',
              address: ''
            },
            officeId: office ? office.id : '',
            accountId: office ? office.accountId : '',
            complianceAuditor: false,
            knowledgeManager: false,
            global: true,
            officeAddress: true,
            atheneumContact: {
              id: '',
              fullName: ''
            },
            atheneumContactId: null
          },
          clientLinks: [],
          address: {
            phones: [
              {
                phoneTypeId: this.props.staticData.CONST.PHONE_TYPES.OFFICE,
                phoneNum: ''
              }
            ],
            emails: [],
            address1: office ? office.address.address1 : '',
            address2: office ? office.address.address2 : '',
            city: office ? office.address.city : '',
            postalCode: office ? office.address.postalCode : '',
            state: office ? office.address.state : '',
            countryId: office ? office.address.countryId : null,
            timezoneId: office ? office.address.timezoneId : null
          },
          country: '',
          email: '',
          kmSpecificAccounts: [{ id: '' }]
        }
  }

  getValidationSchema = () => {
    const { contact } = this.props
    return yup.object().shape({
      firstName: yup
        .string()
        .max(50, 'First name must be between 2 and 50 characters long')
        .min(2, 'First name must be between 2 and 50 characters long')
        .required('First name field is required'),
      lastName: yup
        .string()
        .max(50, 'Last name must be between 2 and 50 characters long')
        .min(2, 'Last name must be between 2 and 50 characters long')
        .required('Last name field is required'),
      client: yup.object().shape({
        complianceAuditor: yup.bool(),
        knowledgeManager: yup.bool(),
        global: yup.bool(),
        office: yup
          .object()
          .typeError(
            'Office field is required, select one of the suggested items'
          )
          .shape({
            id: yup
              .number()
              .typeError(
                'Office field is required, select one of the suggested items'
              )
          })
      }),
      clientLinks: yup.array().of(
        yup.object().shape({
          professionalLinkUrl: yup
            .string()
            .url('The Professional URL format is invalid')
        })
      ),
      address: yup.object().shape({
        city: yup.string().required('City field is required'),
        phones: yup.array().of(
          yup.object().shape({
            phoneTypeId: yup
              .number()
              .integer()
              .typeError('Phone type is required'),
            phoneNum: yup
              .string()
              .matches(
                /^[0-9\-+/\s]*$/,
                "Phone number can contain only digits, '+', '-', ' ' and '/'"
              )
              .required('Phone number is required')
          })
        ),
        emails: yup.array().of(
          yup.object().shape({
            email: yup
              .string()
              .email('Email format is invalid')
              .required('Email is required')
              .test('unique', 'Email is already in use', value => {
                if (yup.string().email().isValidSync(value)) {
                  if (!value) return true // fallback to required error
                  let payload = { email: value }
                  if (contact) {
                    payload = { ...payload, id: contact.id }
                  }
                  return userApi.checkEmail(payload)
                    .then(res => res.valid)
                    .catch(_ => false)
                }
                return true
              })
          })
        )
      }),
      email: yup
        .string()
        .email('Email format is invalid')
        .required('Email is required')
        .test('unique', 'Email is already in use', value => {
          if (yup.string().email().required().isValidSync(value)) {
            if (!value) return true // fallback to required error
            let payload = { email: value }
            if (contact) {
              payload = { ...payload, id: contact.id }
            }
            return userApi.checkEmail(payload)
              .then(res => res.valid)
              .catch(_ => false)
          }
          return true
        }),
      type: yup.string(),
      kmSpecificAccounts: yup.array().when('type', {
        is: 'Specific accounts',
        then: yup.array().of(
          yup.object().shape({
            id: yup
              .number()
              .required(
                'Account field is required, select one of the suggested items.'
              )
              .typeError(
                'Account field is required, select one of the suggested items. If there are no suggested items fill the office field first'
              )
          })
        )
      }),
      emailNotRepeating: yup
        .mixed()
        .test(
          'emailNotRepeating',
          'You are using the same email in multiple fields',
          function () {
            const primaryEmail = this.parent.email
            const restOfEmails = this.parent.address.emails.map(
              email => email.email
            )
            const emails = [primaryEmail, ...restOfEmails]

            const uniqueEmails = uniq(emails)

            return emails.length === uniqueEmails.length
          }
        ),
      phoneNotRepeating: yup
        .mixed()
        .test(
          'phoneNotRepeating',
          'You are using the same phone number in multiple fields',
          function () {
            const phones = compact(
              this.parent.address.phones.map(phone => phone.phoneNum)
            )
            const uniquePhones = compact(uniq(phones))

            return phones.length === uniquePhones.length
          }
        )
    })
  }
}

ClientContactForm.defaultProps = {
  hideButtons: true,
  showCloseIcon: true,
  shouldCloseOnOverlayClick: false
}

export default withRouter(
  inject(
    'sidebarStore',
    'staticData',
    'modalStore',
    'userStore'
  )(observer(ClientContactForm))
)
